import React, { useState, useEffect } from 'react';
import { Send, Terminal, Zap, ShieldCheck, Database, Layers, ArrowRight, Loader2, Target, X, CheckCircle2, Box } from 'lucide-react';
import { API_URL } from '../../config';
import { useNavigate, useSearchParams } from 'react-router-dom';

const Card = ({ children, className }) => <div className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 ${className || ''}`}>{children}</div>;
const CardHeader = ({ children }) => <div className="p-6 pb-4 border-b border-slate-100 dark:border-slate-700/50">{children}</div>;
const CardTitle = ({ children, className }) => <h3 className={`text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2 ${className || ''}`}>{children}</h3>;
const CardContent = ({ children, className }) => <div className={`p-6 ${className || ''}`}>{children}</div>;

export default function ProbeDispatch() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [matrixData, setMatrixData] = useState({ L1: [], L2: [], L3: [], L4: [], L5: [] });
  const [isPushing, setIsPushing] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [showBridgeModal, setShowBridgeModal] = useState(false);
  const [myProjects, setMyProjects] = useState([]);
  const [bridgeTargetId, setBridgeTargetId] = useState('');
  const [bridgeMode, setBridgeMode] = useState('existing');
  const [newProjectData, setNewProjectData] = useState({ name: '', brandName: '', competitors: '' });

  const toggleLevel = (level) => {
    setExpandedLevels(prev => ({...prev, [level]: !prev[level]}));
  };

  useEffect(() => {
    if (!projectId) {
      navigate('/prime/galaxy');
      return;
    }
    
    const executePruning = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('geo_token') || localStorage.getItem('token');
        
        // 我们先调刚才新构想的 API 来执行去重逻辑并获取浓缩后的数据
        const res = await fetch(`${API_URL}/seo/config/prune-intents/${projectId}`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (data.success) {
           setStats(data.stats);
           setMatrixData(data.matrix);
        } else {
           console.error('Pruning failed:', data.error);
        }
      } catch (err) {
        console.error('Error during pruning:', err);
      } finally {
        setLoading(false);
      }
    };
    
    executePruning();
  }, [projectId, navigate]);

  
  const handleOpenBridge = async () => {
    setShowBridgeModal(true);
    setBridgeTargetId(projectId || '');
    try {
      const token = localStorage.getItem('geo_token') || localStorage.getItem('token');
      // Fix URL to standard projects list
      const res = await fetch(`${API_URL}/brand-projects?system=geo`, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      const rawText = await res.text();
      console.log('Raw Project Response:', rawText);
      try {
        const data = JSON.parse(rawText);
        let projArray = [];
        if (Array.isArray(data)) { projArray = data; }
        else if (data.projects) { projArray = data.projects; }
        else if (data.data) { projArray = data.data; }
        console.log('Parsed Projects:', projArray);
        
        // Filter out soft-deleted projects just in case
        projArray = projArray.filter(p => p.status !== 'DELETED');
        setMyProjects(projArray);
      } catch(e) { console.error('Parse err:', e); }
    } catch(e) { console.error('Fetch projects error:', e) }
  };

  const executeBridge = async () => {
    if (bridgeMode === 'new' && (!newProjectData.name || !newProjectData.brandName)) {
        return alert('请填写新项目名称和监控主品牌');
    }
    if (bridgeMode === 'existing' && !bridgeTargetId) {
        return alert('请选择一个目标项目');
    }

    setIsPushing(true);
    try {
      const token = localStorage.getItem('geo_token') || localStorage.getItem('token');
      const payload = {
          sourceProjectId: projectId,
          targetProjectId: bridgeMode === 'existing' ? bridgeTargetId : null,
          newProject: bridgeMode === 'new' ? newProjectData : null,
          matrixData
      };
      
      const res = await fetch(`${API_URL}/seo/config/bridge-to-score`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
         body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
         setShowBridgeModal(false);
         // 简短的延迟制造"数据流转完毕"的体感
         setTimeout(() => {
             navigate(`/settings/brand-competitor?projectId=${data.projectId}`);
         }, 800);
      } else {
         alert('桥接失败: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('同步至序SCORE失败');
    } finally {
      setIsPushing(false);
    }
  };


  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
        <div className="text-lg font-mono text-slate-600 dark:text-slate-300 animate-pulse">正在 1024 维 Qdrant 空间中执行高频重叠意图折叠...</div>
        <p className="text-sm text-slate-500">正在剔除余弦相似度极高的同质化长尾词，确保 API 成本最高效利用。</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Terminal className="w-8 h-8 text-blue-500" />
            智算探针集群 (Concentrated Probe Cluster)
          </h1>
          <p className="text-slate-500 mt-2">已成功完成基于高维向量的语义去重剪枝。以下为将下发至 序 SCORE 的最终高质量自然语言探针库。</p>
        </div>
      </div>

      {/* 战报概览区 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-none shadow-xl">
          <CardContent className="flex flex-col items-center py-8 text-white">
            <Database className="w-10 h-10 text-indigo-400 mb-4" />
            <div className="text-4xl font-bold font-mono">{stats?.originalCount || 0}</div>
            <div className="text-slate-400 mt-2 font-medium">原始矩阵生成探针数</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500/10 to-pink-500/10 border-red-500/20 shadow-inner">
          <CardContent className="flex flex-col items-center py-8">
            <ShieldCheck className="w-10 h-10 text-pink-500 mb-4" />
            <div className="text-4xl font-bold font-mono text-pink-600 dark:text-pink-400">-{stats?.prunedCount || 0}</div>
            <div className="text-slate-600 dark:text-slate-400 mt-2 font-medium">语义重合被剪枝数</div>
            <div className="text-xs text-slate-400 mt-1">Cosine Similarity &gt; 0.95</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/20 shadow-xl relative overflow-hidden">
          <div className="absolute -right-6 -top-6 text-emerald-500/10">
            <Target className="w-48 h-48" />
          </div>
          <CardContent className="flex flex-col items-center py-8 relative z-10">
            <Zap className="w-10 h-10 text-emerald-500 mb-4" />
            <div className="text-4xl font-bold font-mono text-emerald-600 dark:text-emerald-400">{stats?.finalCount || 0}</div>
            <div className="text-slate-700 dark:text-slate-300 mt-2 font-bold">最终高浓缩有效探针</div>
          </CardContent>
        </Card>
      </div>

      {/* L1-L5 明细矩阵展示区 */}
      <Card>
        <CardHeader>
           <CardTitle><Layers className="w-5 h-5 text-indigo-500" /> 高敏探针矩阵库 (降水版)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
           <div className="overflow-hidden">
             {['L5', 'L4', 'L3', 'L2', 'L1'].map((level) => {
               const intents = matrixData[level] || [];
               if (intents.length === 0) return null;
               
               // 根据 Level 设定颜色
               let badgeColor = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
               if (level === 'L5') badgeColor = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-500/30';
               if (level === 'L4') badgeColor = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-500/30';
               if (level === 'L3') badgeColor = 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400 border-pink-500/30';
               if (level === 'L2') badgeColor = 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-500/30';
               if (level === 'L1') badgeColor = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-500/30';

               return (
                 <div key={level} className="border-b border-slate-100 dark:border-slate-700 last:border-0 p-6 flex flex-col md:flex-row gap-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <div className="md:w-32 flex-shrink-0">
                       <span className={`inline-flex items-center justify-center px-3 py-1 rounded-md text-sm font-bold border ${badgeColor}`}>
                         {level} (剩余: {intents.length})
                       </span>
                    </div>
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-3">
                       
                       {(expandedLevels[level] ? intents : intents.slice(0, 8)).map((intent, idx) => {
                         const cleanQuery = intent.query.replace(/^\[.*?\]\s*/, '');
                         return (
                           <div key={idx} className="flex flex-col bg-slate-50 dark:bg-slate-900 border border-slate-200/60 dark:border-slate-700/60 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                              { (level !== 'L1' && level !== 'L2') && (
                                <div className="mb-3 flex items-center">
                                  <span className="px-2.5 py-1 rounded text-[11px] font-bold tracking-wide bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 border border-indigo-200/50 dark:border-indigo-500/20">
                                    {intent.persona}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed">{cleanQuery}</span>
                           </div>
                         );
                       })}
                       {intents.length > 8 && (
                          <button 
                            onClick={() => toggleLevel(level)}
                            className="flex flex-col items-center justify-center text-sm font-medium text-indigo-500 hover:text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-500/5 dark:hover:bg-indigo-500/10 rounded-lg border border-dashed border-indigo-200/80 dark:border-indigo-500/30 transition-colors p-4 min-h-[100px] w-full col-span-1 lg:col-span-2"
                          >
                             {expandedLevels[level] ? '收起列表' : `展开查看剩余 ${intents.length - 8} 条探针 (建议发布前确认)`}
                          </button>
                       )}
                    </div>
                 </div>
               );
             })}
           </div>
        </CardContent>
      </Card>

      {/* 底部点火按钮 */}
      <div className="mt-12 flex justify-end pb-12 border-t border-slate-200 dark:border-slate-800/60 pt-8">
        <button
          onClick={handleOpenBridge}
          disabled={isPushing}
          className="group relative flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:via-indigo-500 hover:to-purple-500 text-white font-bold text-lg rounded-2xl shadow-[0_0_20px_rgba(79,70,229,0.3)] hover:shadow-[0_0_30px_rgba(79,70,229,0.5)] transition-all hover:-translate-y-1 disabled:opacity-50 disabled:hover:-translate-y-0"
        >
          {isPushing ? (
            <><Loader2 className="w-6 h-6 animate-spin" /> 正在桥接至序 SCORE 数据阵列...</>
          ) : (
            <>
              <Database className="w-6 h-6 text-blue-200" />
              <span>无缝桥接至 序 SCORE (品牌监控矩阵)</span>
              <ArrowRight className="w-6 h-6 ml-2 text-blue-200 group-hover:translate-x-2 transition-transform" />
            </>
          )}
        </button>
      </div>



      {/* 桥接部署弹窗 Modal */}
      {showBridgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 p-4">
          <div className="bg-white dark:bg-slate-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/80">
               <div>
                 <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Database className="w-5 h-5 text-indigo-500" />
                    目标数据舱部署 (SCORE Destination)
                 </h2>
                 <p className="text-sm text-slate-500 mt-1">
                   意图探针矩阵与竞品配置将被结构化重塑为 SCORE 的业务维度树
                 </p>
               </div>
               <button onClick={() => setShowBridgeModal(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
                 <X className="w-5 h-5" />
               </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
               {/* Tab 切换 */}
               <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-900 rounded-lg mb-6">
                 <button 
                   onClick={() => setBridgeMode('existing')}
                   className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${bridgeMode === 'existing' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                 >
                   注入现有监控项目
                 </button>
                 <button 
                   onClick={() => setBridgeMode('new')}
                   className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${bridgeMode === 'new' ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                 >
                   + 裂变全新独立项目
                 </button>
               </div>

               {/* 表单内容 */}
               {bridgeMode === 'existing' ? (
                 <div className="space-y-4">
                   <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">请选择目标 SCORE 项目</label>
                   <select 
                     value={bridgeTargetId} 
                     onChange={(e) => setBridgeTargetId(e.target.value)}
                     style={{ minHeight: "64px", height: "64px" }} className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-4 text-base font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm transition-all hover:border-indigo-400 cursor-pointer"
                   >
                     <option value="">-- 请选择数据归属项目 --</option>
                     {myProjects.map(p => (
                       <option key={p.id} value={p.id}>{p.name}</option>
                     ))}
                   </select>
                   
                   {bridgeTargetId === projectId && (
                     <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-4 mt-4 animate-in fade-in slide-in-from-top-2">
                        <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-1">
                          <Zap className="w-4 h-4" /> 大破大立 (Clean Sweep & Replace)
                        </h4>
                        <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
                          检测到您正将终极矩阵覆盖回当前项目。系统将<strong>自动执行词库清洗</strong>，销毁早期的临时草稿词，并完全重塑为当前的 L1-L5 业务维度树，确保最终词库绝对纯净。
                        </p>
                     </div>
                   )}
                   <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-lg p-4 mt-4">
                      <h4 className="text-sm font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-4 h-4" /> 结构化挂载说明
                      </h4>
                      <ul className="text-sm text-indigo-700 dark:text-indigo-400 space-y-1 ml-6 list-disc">
                        <li>L1 宏观词汇将自动创建为 <strong>一级业务维度</strong></li>
                        <li>人群画像标签将自动创建为 <strong>二级业务子分类</strong></li>
                        <li>探针词库将精准挂载到对应的维度叶子节点</li>
                      </ul>
                   </div>
                 </div>
               ) : (
                 <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                   <div>
                     <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">新项目名称 (Project Name)</label>
                     <input 
                       type="text" placeholder="例如：2026 Q3 护肤品大促防守监测"
                       value={newProjectData.name} onChange={e => setNewProjectData({...newProjectData, name: e.target.value})}
                       className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">监控主品牌 (Main Brand)</label>
                     <input 
                       type="text" placeholder="主攻或防守的核心品牌"
                       value={newProjectData.brandName} onChange={e => setNewProjectData({...newProjectData, brandName: e.target.value})}
                       className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">直接挂载核心竞品 (Competitors)</label>
                     <input 
                       type="text" placeholder="逗号分隔，如：兰蔻, 雅诗兰黛, SK-II"
                       value={newProjectData.competitors} onChange={e => setNewProjectData({...newProjectData, competitors: e.target.value})}
                       className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                     />
                     <p className="text-xs text-slate-500 mt-1">系统将同步为您在 SCORE 初始化这些竞品卡片。</p>
                   </div>
                 </div>
               )}
            </div>

            <div className="p-6 border-t border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800 flex justify-end gap-3">
              <button 
                onClick={() => setShowBridgeModal(false)}
                className="px-6 py-2.5 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                稍后再说
              </button>
              <button 
                onClick={executeBridge}
                disabled={isPushing || (bridgeMode === 'new' && (!newProjectData.name || !newProjectData.brandName)) || (bridgeMode === 'existing' && !bridgeTargetId)}
                className="flex items-center gap-2 px-8 py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md transition-colors disabled:opacity-50"
              >
                {isPushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Box className="w-4 h-4" />}
                {isPushing ? '数据挂载中...' : '确认部署并跳转至 SCORE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
